import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { WorkspaceLocation } from 'honeygrid-types'
import { MapPin, Star, Phone, Globe, Plus } from 'lucide-react'

interface LocationsCardsProps {
	locations: WorkspaceLocation[]
	onSelectLocation: (location: WorkspaceLocation) => void
	onAddLocation: () => void
	selectedLocationId?: string
}

export function LocationsCards({ locations, onSelectLocation, onAddLocation, selectedLocationId }: LocationsCardsProps) {
	if (locations.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center text-center space-y-3 bg-muted/50 h-40 py-8">
				<div className="h-8 w-8 shrink-0 flex items-center justify-center">
					<MapPin className="h-8 w-8 text-foreground/60" />
				</div>
				<p className="text-sm font-medium text-muted-foreground">No locations yet</p>
                <Button variant="outline" onClick={onAddLocation}>
					<Plus className="h-4 w-4 mr-2" />
					Add Location
				</Button>
			</div>
		)
	}

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
			{locations.map((location) => (
				<Card
					key={location.uuid}
					className={`cursor-pointer hover:shadow-md transition-shadow ${
						selectedLocationId === location.uuid ? 'ring-2 ring-primary' : ''
					}`}
					onClick={() => onSelectLocation(location)}
				>
					<CardContent className="p-4">
						<div className="space-y-3">
							<div className="flex items-start gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
									<MapPin className="h-5 w-5" />
								</div>
								<div className="flex-1 min-w-0">
									<div className="font-medium truncate">{location.name}</div>
									<div className="text-sm text-muted-foreground truncate">
										{Array.isArray(location.types) && location.types.length > 0
											? location.types[0]
											: 'Business'}
									</div>
								</div>
							</div>

							<div className="space-y-2 text-sm">
								<div className="flex items-center gap-2">
									<MapPin className="h-3 w-3 text-muted-foreground" />
									<span className="truncate">{location.address}</span>
								</div>
								
								{location.phone_number && (
									<div className="flex items-center gap-2">
										<Phone className="h-3 w-3 text-muted-foreground" />
										<span>{location.phone_number}</span>
									</div>
								)}
								
								<div className="flex items-center gap-2">
									<Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
									<span>{location.rating}</span>
									<span className="text-muted-foreground">
										({location.ratings_total} reviews)
									</span>
								</div>
								
								{location.website && (
									<div className="flex items-center gap-2">
										<Globe className="h-3 w-3 text-muted-foreground" />
										<span className="truncate">{location.website}</span>
									</div>
								)}
							</div>
						</div>
					</CardContent>
				</Card>
			))}
		</div>
	)
}
